// src/components/assessment/AssessmentForm.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
    TextField, Button, Paper, Typography, Grid,
    FormControl, InputLabel, Select, MenuItem, CircularProgress,
    Box, Stepper, Step, StepLabel, Divider, Alert
} from '@mui/material';
import { createAssessment } from '../../services/assessmentService';
import RiskFactorInput from './RiskFactorInput';

const validationSchema = Yup.object({
    title: Yup.string().required('Title is required'),
    domain: Yup.string().required('Domain is required'),
});

const AssessmentForm = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [activeStep, setActiveStep] = useState(0);

    const steps = ['Assessment Details', 'Risk Factors', 'Review & Submit'];

    const formik = useFormik({
        initialValues: {
            title: '',
            domain: '',
            financialData: {
                annualIncome: '',
                totalDebt: '',
                creditHistory: []
            },
            securityData: {
                systemDetails: '',
                existingControls: '',
                vulnerabilities: []
            },
            healthData: {
                facilityType: '',
                occupancy: '',
                hazards: []
            },
            operationalData: {
                processType: '',
                resourceAvailability: '',
                dependencies: []
            }
        },
        validationSchema,
        onSubmit: async (values) => {
            setLoading(true);
            setError(null);
            try {
                // Transform form data to API expected format
                const assessmentData = {
                    title: values.title,
                    domain: values.domain,
                    input_data: {
                        financial_data: values.domain === 'financial' ? {
                            annual_income: parseFloat(values.financialData.annualIncome) || 0,
                            total_debt: parseFloat(values.financialData.totalDebt) || 0,
                            credit_history: values.financialData.creditHistory
                        } : undefined,
                        security_data: values.domain === 'cybersecurity' ? {
                            system_details: values.securityData.systemDetails,
                            existing_controls: values.securityData.existingControls,
                            vulnerabilities: values.securityData.vulnerabilities
                        } : undefined,
                        health_data: values.domain === 'health' ? {
                            facility_type: values.healthData.facilityType,
                            occupancy: parseInt(values.healthData.occupancy) || 0,
                            hazards: values.healthData.hazards
                        } : undefined,
                        operational_data: values.domain === 'operational' ? {
                            process_type: values.operationalData.processType,
                            resource_availability: values.operationalData.resourceAvailability,
                            dependencies: values.operationalData.dependencies
                        } : undefined
                    }
                };

                const result = await createAssessment(assessmentData);
                navigate(`/assessment/${result.id}`);
            } catch (err) {
                setError(err.message || 'Failed to create assessment');
                setActiveStep(0); // Go back to first step on error
            } finally {
                setLoading(false);
            }
        },
    });

    const handleNext = () => {
        if (activeStep === 0) {
            // Validate first step fields
            const errors = {};
            if (!formik.values.title) errors.title = 'Title is required';
            if (!formik.values.domain) errors.domain = 'Domain is required';

            if (Object.keys(errors).length > 0) {
                formik.setErrors(errors);
                return;
            }
        }

        setActiveStep((prevStep) => prevStep + 1);

        // Submit on last step
        if (activeStep === steps.length - 2) {
            formik.submitForm();
        }
    };

    const handleBack = () => {
        setActiveStep((prevStep) => prevStep - 1);
    };

    const getStepContent = (step) => {
        switch (step) {
            case 0:
                return (
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                id="title"
                                name="title"
                                label="Assessment Title"
                                value={formik.values.title}
                                onChange={formik.handleChange}
                                error={formik.touched.title && Boolean(formik.errors.title)}
                                helperText={formik.touched.title && formik.errors.title}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <FormControl fullWidth error={formik.touched.domain && Boolean(formik.errors.domain)}>
                                <InputLabel id="domain-label">Risk Domain</InputLabel>
                                <Select
                                    labelId="domain-label"
                                    id="domain"
                                    name="domain"
                                    label="Risk Domain"
                                    value={formik.values.domain}
                                    onChange={formik.handleChange}
                                >
                                    <MenuItem value="financial">Financial</MenuItem>
                                    <MenuItem value="cybersecurity">Cybersecurity</MenuItem>
                                    <MenuItem value="health">Health & Safety</MenuItem>
                                    <MenuItem value="operational">Operational</MenuItem>
                                </Select>
                                {formik.touched.domain && formik.errors.domain && (
                                    <Typography variant="caption" color="error">
                                        {formik.errors.domain}
                                    </Typography>
                                )}
                            </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">
                                Select the risk domain that best matches your assessment needs. Each domain will have specific risk factors to evaluate.
                            </Typography>
                        </Grid>
                    </Grid>
                );
            case 1:
                return (
                    <RiskFactorInput
                        domain={formik.values.domain}
                        values={formik.values}
                        handleChange={formik.handleChange}
                        setFieldValue={formik.setFieldValue}
                        errors={formik.errors}
                        touched={formik.touched}
                    />
                );
            case 2:
                return (
                    <Box>
                        <Typography variant="h6" gutterBottom>
                            Review Assessment Details
                        </Typography>
                        <Divider sx={{ mb: 2 }} />

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2">Assessment Title:</Typography>
                                <Typography variant="body1" sx={{ mb: 2 }}>
                                    {formik.values.title}
                                </Typography>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <Typography variant="subtitle2">Risk Domain:</Typography>
                                <Typography variant="body1" sx={{ mb: 2 }}>
                                    {formik.values.domain.charAt(0).toUpperCase() + formik.values.domain.slice(1)}
                                </Typography>
                            </Grid>

                            {/* Render domain-specific review fields */}
                            {formik.values.domain === 'financial' && (
                                <>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2">Annual Income:</Typography>
                                        <Typography variant="body1">
                                            ${parseFloat(formik.values.financialData.annualIncome).toLocaleString() || 'Not specified'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2">Total Debt:</Typography>
                                        <Typography variant="body1">
                                            ${parseFloat(formik.values.financialData.totalDebt).toLocaleString() || 'Not specified'}
                                        </Typography>
                                    </Grid>
                                </>
                            )}

                            {formik.values.domain === 'cybersecurity' && (
                                <>
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2">System Details:</Typography>
                                        <Typography variant="body1">
                                            {formik.values.securityData.systemDetails || 'Not specified'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2">Existing Controls:</Typography>
                                        <Typography variant="body1">
                                            {formik.values.securityData.existingControls || 'Not specified'}
                                        </Typography>
                                    </Grid>
                                </>
                            )}

                            {formik.values.domain === 'health' && (
                                <>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2">Facility Type:</Typography>
                                        <Typography variant="body1">
                                            {formik.values.healthData.facilityType || 'Not specified'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2">Occupancy:</Typography>
                                        <Typography variant="body1">
                                            {formik.values.healthData.occupancy || 'Not specified'}
                                        </Typography>
                                    </Grid>
                                </>
                            )}

                            {formik.values.domain === 'operational' && (
                                <>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2">Process Type:</Typography>
                                        <Typography variant="body1">
                                            {formik.values.operationalData.processType || 'Not specified'}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="subtitle2">Resource Availability:</Typography>
                                        <Typography variant="body1">
                                            {formik.values.operationalData.resourceAvailability || 'Not specified'}
                                        </Typography>
                                    </Grid>
                                </>
                            )}
                        </Grid>

                        <Box sx={{ mt: 3, mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                After submission, our system will analyze your data using AI-powered risk assessment algorithms to generate a comprehensive risk report.
                            </Typography>
                        </Box>
                    </Box>
                );
            default:
                return 'Unknown step';
        }
    };

    return (
        <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
                New Risk Assessment
            </Typography>

            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                {steps.map((label) => (
                    <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                    </Step>
                ))}
            </Stepper>

            <form onSubmit={formik.handleSubmit}>
                {getStepContent(activeStep)}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                    <Button
                        variant="outlined"
                        onClick={handleBack}
                        disabled={activeStep === 0 || loading}
                    >
                        Back
                    </Button>

                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleNext}
                        disabled={loading}
                        startIcon={loading && activeStep === steps.length - 1 ? <CircularProgress size={20} /> : null}
                    >
                        {activeStep === steps.length - 1 ? 'Submit' : 'Next'}
                    </Button>
                </Box>
            </form>
        </Paper>
    );
};

export default AssessmentForm;